import React, { useEffect, useMemo, useState } from 'react';

import { Modal as ModalAntD } from 'antd';
import { T } from 'react-translator-component';
import styled from 'styled-components';

import { refusalMessage } from './inviteRejection';
import {
  bulkLabelFor,
  filterAccounts,
  isExclusionMode,
  excludedCount,
  resolveScope,
  ScopeMode,
  scopeTotals,
} from './scopeSelection';
import { wrongTokenMessage } from './tokenKind';

import {
  generateInvite,
  getHierarchyAccounts,
  resolveSharePartner,
} from '../../../../hooks/interoperability';
import {
  GeneratedInvite,
  InviteTtl,
  ScopeAccount,
  SharePartner,
} from '../../../../types/responses/Interoperability';
import { PartnerLogo } from '../../../molecules/PartnerLogo';
import { directionLabel } from '../contractRules';

const Foot = styled.div`
  padding: 14px 20px;
  border-top: 1px solid #e9ebee;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Label = styled.label`
  display: block;
  font-size: 11.5px;
  color: #4a5462;
  margin: 12px 0 4px;
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid #d9dce1;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 12.5px;
`;

const Cards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-bottom: 10px;
`;

const ModeCard = styled.button<{ selected: boolean }>`
  text-align: left;
  background: ${({ selected }) => (selected ? '#f6f9ff' : '#fff')};
  border: 1px solid ${({ selected }) => (selected ? '#2f6fe0' : '#d9dce1')};
  border-radius: 8px;
  padding: 8px 10px;
  cursor: pointer;
  font-size: 11.5px;
`;

const AccountRow = styled.div<{ marked: boolean; exclusion: boolean; disabled: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 11.5px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  background: ${({ marked, exclusion }) =>
    marked ? (exclusion ? '#fdf4f4' : '#f6f9ff') : '#fff'};
`;

const Mark = styled.span<{ marked: boolean; exclusion: boolean }>`
  width: 14px;
  height: 14px;
  flex: none;
  border-radius: 3px;
  text-align: center;
  line-height: 12px;
  font-size: 10px;
  color: #fff;
  border: 1.4px solid
    ${({ marked, exclusion, theme }) =>
      (marked ? (exclusion ? '#c8352f' : theme.primary) : '#c6cbd3')};
  background: ${({ marked, exclusion, theme }) =>
    marked ? (exclusion ? '#c8352f' : theme.primary) : '#fff'};
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 8px 0;
`;

const Chip = styled.span<{ bg: string; fg: string }>`
  background: ${({ bg }) => bg};
  color: ${({ fg }) => fg};
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 10.5px;
`;

const Totals = styled.div`
  font-size: 11.5px;
  color: #4a5462;
  border-top: 1px solid #eef0f2;
  padding-top: 8px;
  margin-top: 8px;
`;

const TokenRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const TokenInput = styled.input<{ invalid: boolean }>`
  flex: 1;
  min-width: 0;
  border: 1px solid ${({ invalid }) => (invalid ? '#c8352f' : '#d9dce1')};
  border-radius: 6px;
  padding: 8px 10px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 12.5px;
  color: #12263f;
  outline: none;
`;

const TokenMessage = styled.div<{ error: boolean }>`
  margin-top: 6px;
  font-size: 11px;
  line-height: 1.5;
  color: ${({ error }) => (error ? '#b02525' : '#8a9099')};
`;

const Resolved = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
  padding: 8px 10px;
  background: #f6f8fa;
  border: 1px solid #e3e6ea;
  border-radius: 6px;
`;

const ResolvedName = styled.div`
  font-weight: 600;
  font-size: 12.5px;
  color: #12263f;
`;

const AppNote = styled.p<{ hasApp: boolean }>`
  background: ${({ hasApp }) => (hasApp ? '#fff' : '#fffdf6')};
  border: 1px solid ${({ hasApp }) => (hasApp ? '#e3e6ea' : '#eddfb8')};
  color: ${({ hasApp }) => (hasApp ? '#4a5462' : '#6b5a2a')};
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 11px;
  margin: 8px 0 0;
`;

const Pills = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const Pill = styled.button<{ selected: boolean; enabled?: boolean }>`
  background: ${({ selected, theme }) => (selected ? theme.primary : '#fff')};
  color: ${({ selected }) => (selected ? '#fff' : '#4a5462')};
  border: 1px solid ${({ selected, theme }) => (selected ? theme.primary : '#d9dce1')};
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 11.5px;
  cursor: ${({ enabled = true }) => (enabled ? 'pointer' : 'not-allowed')};
  opacity: ${({ enabled = true }) => (enabled ? 1 : 0.5)};
`;

const Required = styled.p`
  font-size: 10.5px;
  color: #8a9099;
  margin: 0;
  flex: 1;
`;

const Primary = styled.button`
  background: ${({ theme }) => theme.primary};
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 9px 16px;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Secondary = styled.button`
  background: #fff;
  color: #4a5462;
  border: 1px solid #d9dce1;
  border-radius: 6px;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 11.5px;
  white-space: nowrap;

  &:disabled {
    color: #a9b4c2;
    cursor: not-allowed;
  }
`;

const Cancel = styled.button`
  background: #fff;
  color: #c8352f;
  border: 1px solid #f0c9c7;
  border-radius: 6px;
  padding: 9px 16px;
  cursor: pointer;
`;

// O @types do antd 4.10 nao declara children no Modal; mesmo alias que o repo usa.
const Modal: any = ModalAntD;

const TTLS: InviteTtl[] = ['MIN_15', 'HOUR_1', 'HOURS_24', 'DAYS_7'];
const TTL_LABEL: Record<InviteTtl, string> = {
  MIN_15: '15 min',
  HOUR_1: '1 hora',
  HOURS_24: '24 horas',
  DAYS_7: '7 dias',
};

export type InviteSummary = {
  accountCount: number;
  stationCount: number;
  inheritsNewSubaccounts: boolean;
  ttlLabel: string;
};

export type InvitePartnerModalProps = {
  tenantId: string;
  onClose: () => void;
  /**
   * Recebe o convite gerado junto do resumo do que foi escolhido. O resumo vai
   * daqui porque a tela do token nao pode reconsultar o convite: nenhum
   * endpoint devolve o token depois da geracao.
   */
  onGenerated: (invite: GeneratedInvite, summary: InviteSummary) => void;
};

/**
 * Modal de convidar parceiro (MV2-1714).
 *
 * O compartilhamento é sempre de todas as estações de uma conta, então a
 * escolha acontece na granularidade de conta da hierarquia.
 */
export const InvitePartnerModal: React.FC<InvitePartnerModalProps> = ({
  tenantId,
  onClose,
  onGenerated,
}) => {
  const [accounts, setAccounts] = useState<ScopeAccount[]>([]);

  /** Token que a conta parceira entregou, e a conta que ele identifica. */
  const [partnerToken, setPartnerToken] = useState('');
  const [partner, setPartner] = useState<SharePartner | null>(null);
  const [resolving, setResolving] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [email, setEmail] = useState('');
  const [mode, setMode] = useState<ScopeMode>('ALL');
  const [marked, setMarked] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [inherits, setInherits] = useState(false);
  const [bidirectional, setBidirectional] = useState(false);
  const [ttl, setTtl] = useState<InviteTtl>('HOURS_24');
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState('');

  useEffect(() => {
    (async () => {
      setAccounts(await getHierarchyAccounts(tenantId));
    })();
  }, [tenantId]);

  const partnerHasApp = !!partner?.has_app;

  /**
   * Ja existe convite valido para este parceiro (MV2-1815).
   *
   * Bloqueia aqui, na confirmacao de quem e o parceiro, e nao no botao de gerar: a
   * pessoa descobre antes de escolher escopo, prazo e tipo de contrato. O backend
   * recusa de novo na geracao — isto e conveniencia, nao a regra.
   */
  const partnerHasPendingInvite = !!partner?.pending_invite;

  /**
   * Resolve o token para saber de quem e, antes de gerar o convite.
   *
   * A confirmacao do nome vem antes de qualquer escolha de escopo: convidar a conta
   * errada e um erro caro, porque o convite passa a valer para ela e nao ha como
   * redirecionar. O servico ja recusa o proprio token e conta da propria
   * hierarquia, e a mensagem dele e a que aparece aqui.
   */
  const resolvePartner = async () => {
    const typed = partnerToken.trim();

    if (!typed) {
      return;
    }

    // Token de convite colado aqui e o engano simetrico ao do outro modal.
    const mismatch = wrongTokenMessage(typed, 'ACCOUNT');

    if (mismatch) {
      setPartner(null);
      setTokenError(mismatch);
      return;
    }

    try {
      setResolving(true);
      setTokenError('');
      setPartner(await resolveSharePartner(tenantId, typed));
    } catch (error) {
      setPartner(null);
      setTokenError(
        refusalMessage(error) || T('Could not check this token now. Try again in a moment.'),
      );
    } finally {
      setResolving(false);
    }
  };

  /**
   * Sem app o parceiro não recebe estações: bidirecional é impossível. Se ele
   * já estava marcado e o usuário troca para um parceiro sem app, cai para
   * unilateral sozinho — senão enviaria um contrato que o backend recusa.
   */
  useEffect(() => {
    if (!partnerHasApp && bidirectional) {
      setBidirectional(false);
    }
  }, [partnerHasApp, bidirectional]);

  const subAccounts = useMemo(
    () => accounts.filter((account) => !account.root),
    [accounts],
  );
  const visible = useMemo(
    () => filterAccounts(subAccounts, search),
    [subAccounts, search],
  );

  const scope = useMemo(() => resolveScope(accounts, mode, marked), [accounts, mode, marked]);
  const totals = useMemo(() => scopeTotals(accounts, scope), [accounts, scope]);

  // Quantas contas a exclusao tirou DE FATO: com a cascata (MV2-1847), marcar uma
  // filial pode tirar tres contas, e o chip diria "1" ao lado de um total que ja desceu.
  const foraDoContrato = useMemo(() => excludedCount(accounts, scope), [accounts, scope]);

  const exclusion = isExclusionMode(mode);
  const bulkLabel = bulkLabelFor(mode);

  /** Trocar de modo limpa a busca e a marcação: elas significam outra coisa. */
  const changeMode = (next: ScopeMode) => {
    setMode(next);
    setMarked(new Set());
    setSearch('');
  };

  const toggleAccount = (accountId: string) => {
    if (mode === 'ALL') return;

    setMarked((current) => {
      const next = new Set(current);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  };

  const submit = async () => {
    try {
      setSubmitting(true);
      setFailed('');

      const generated = await generateInvite(tenantId, {
        partner_share_token: partnerToken.trim(),
        contact_email: email,
        contract_type: bidirectional ? 'BIDIRECTIONAL' : 'UNILATERAL',
        ttl,
        scope_mode: mode,
        scope_selection: Array.from(marked),
        inherits_new_subaccounts: inherits,
      });

      onGenerated(generated, {
        accountCount: totals.accountCount,
        stationCount: totals.stationCount,
        inheritsNewSubaccounts: inherits,
        ttlLabel: TTL_LABEL[ttl],
      });
    } catch (error) {
      setFailed(refusalMessage(error) || T('Could not generate the invite. Try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = !!partner && !partnerHasPendingInvite && !!email.trim() && !submitting;

  return (
    /*
      Modal do antd: Esc fecha, o foco fica preso e o role="dialog" vem de graca —
      o overlay proprio nao tinha nenhum dos tres. O footer fica proprio porque
      carrega a nota de campos obrigatorios ao lado dos botoes.
    */
    <Modal
      visible
      width={560}
      title={T('Invite partner')}
      onCancel={onClose}
      footer={
        <Foot>
          <Required>
            {T('* Required fields. The partner only sees your network after entering the token.')}
          </Required>
          <Cancel type="button" onClick={onClose}>{T('Cancel')}</Cancel>
          <Primary type="button" disabled={!canSubmit} onClick={submit}>
            {T('Generate invite')}
          </Primary>
        </Foot>
      }
    >
      <div>
          <Label>{T('Partner account token')}*</Label>
          <TokenRow>
            <TokenInput
              value={partnerToken}
              invalid={!!tokenError}
              spellCheck={false}
              autoComplete="off"
              placeholder="move_acc_••••••••••••••••••••"
              onChange={(event) => {
                setPartnerToken(event.target.value);
                setPartner(null);
                setTokenError('');
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  resolvePartner();
                }
              }}
            />
            <Secondary
              type="button"
              onClick={resolvePartner}
              disabled={resolving || !partnerToken.trim()}
            >
              {resolving ? T('Checking...') : T('Check')}
            </Secondary>
          </TokenRow>

          {tokenError && <TokenMessage error>{tokenError}</TokenMessage>}

          {!tokenError && !partner && (
            <TokenMessage error={false}>
              {T('Ask the partner for the token shown on their Interoperability screen.')}
            </TokenMessage>
          )}

          {partner && (
            <Resolved>
              <PartnerLogo name={partner.name || ''} alias={null} />
              <div>
                <ResolvedName>{partner.name}</ResolvedName>
                <TokenMessage error={false}>
                  {partner.has_app ? T('app configured') : T('no app')}
                </TokenMessage>
              </div>
            </Resolved>
          )}

          {/*
            O parceiro esta certo, mas ja tem convite em aberto: a mensagem fica no lugar
            do erro de token porque o token nao tem nada de errado, e diz onde revogar o
            anterior — o caminho de volta e em outra aba.
          */}
          {partnerHasPendingInvite && (
            <TokenMessage error>
              {T(
                'There is already an active invite for this partner. To generate a new one, revoke the previous one on the Invites tab.',
              )}
            </TokenMessage>
          )}

          <Label>{T('Contact e-mail')}*</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="parceiro@exemplo.com"
          />

          <Label>
            {T('Accounts in the contract')} —{' '}
            {T('sharing always covers every station of the account')}
          </Label>

          <Cards>
            <ModeCard type="button" selected={mode === 'ALL'} onClick={() => changeMode('ALL')}>
              <strong>{T('All accounts')}</strong>
            </ModeCard>
            <ModeCard type="button" selected={mode === 'ALL_EXCEPT'} onClick={() => changeMode('ALL_EXCEPT')}>
              <strong>{T('All except...')}</strong>
            </ModeCard>
            <ModeCard type="button" selected={mode === 'SELECTED'} onClick={() => changeMode('SELECTED')}>
              <strong>{T('Select accounts')}</strong>
            </ModeCard>
          </Cards>

          <Input
            value={search}
            disabled={mode === 'ALL'}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={
              mode === 'ALL'
                ? T('All accounts included — nothing to filter')
                : T('Search sub-account by name')
            }
          />

          {bulkLabel && (
            <button
              type="button"
              onClick={() =>
                setMarked(
                  mode === 'SELECTED'
                    ? new Set(subAccounts.map((account) => account.tenant_id))
                    : new Set(),
                )
              }
              style={{ background: 'none', border: 'none', color: '#2f6fe0', cursor: 'pointer', padding: '6px 0' }}
            >
              {T(bulkLabel)}
            </button>
          )}

          <div style={{ maxHeight: 160, overflowY: 'auto', opacity: mode === 'ALL' ? 0.5 : 1 }}>
            {visible.map((account) => {
              // No "todas, exceto", a conta arrastada pela exclusao da mae aparece
              // excluida como as outras: sem isso a linha dizia que ela esta dentro
              // enquanto o total ao lado ja a tinha tirado (MV2-1847).
              const isMarked = exclusion
                ? !scope.has(account.tenant_id)
                : marked.has(account.tenant_id);
              return (
                <AccountRow
                  key={account.tenant_id}
                  marked={isMarked}
                  exclusion={exclusion}
                  disabled={mode === 'ALL'}
                  onClick={() => toggleAccount(account.tenant_id)}
                >
                  <Mark marked={isMarked} exclusion={exclusion}>
                    {isMarked ? (exclusion ? '×' : '✓') : ''}
                  </Mark>
                  <span>{account.name}</span>
                  <span style={{ marginLeft: 'auto', color: '#6b7280' }}>
                    {account.station_count}
                  </span>
                </AccountRow>
              );
            })}

            {!visible.length && search.trim() && (
              <div style={{ padding: 8, color: '#8a9099', fontSize: 11.5 }}>
                {T('No sub-account with that name')}
              </div>
            )}
          </div>

          <Chips>
            <Chip bg="#eef4fe" fg="#1c4fae">{T('Parent account always included')}</Chip>
            {mode === 'ALL' && (
              <Chip bg="#e7f6ed" fg="#1e7a45">
                {subAccounts.length} {T('sub-accounts included automatically')}
              </Chip>
            )}
            {mode === 'ALL_EXCEPT' && foraDoContrato > 0 && (
              <Chip bg="#fdecec" fg="#b02525">
                {foraDoContrato} {T('out of the contract')}
              </Chip>
            )}
            {mode === 'SELECTED' && (
              <Chip bg="#eef0f2" fg="#6b7280">
                {marked.size} {T('sub-accounts marked')}
              </Chip>
            )}
          </Chips>

          <Totals>
            {totals.accountCount} {T('accounts')} · {totals.stationCount} {T('stations')}
          </Totals>

          <Label>
            <input
              type="checkbox"
              checked={inherits}
              onChange={(e) => setInherits(e.target.checked)}
            />{' '}
            {T('Automatically include new sub-accounts')}
          </Label>

          <Label>{T('Visibility contract type')}</Label>
          <Pills>
            {/*
              Aqui só existem dois dos três tipos: quem convida é quem compartilha, então o
              unilateral deste lado é sempre "→ eu compartilho". Para receber sem compartilhar,
              é o parceiro que precisa convidar — e daí o contrato aparece como "←" na listagem.
            */}
            <Pill type="button" selected={!bidirectional} onClick={() => setBidirectional(false)}>
              {directionLabel('SHARING')}
            </Pill>
            <Pill
              type="button"
              selected={bidirectional}
              enabled={partnerHasApp}
              disabled={!partnerHasApp}
              onClick={() => partnerHasApp && setBidirectional(true)}
            >
              {directionLabel('BIDIRECTIONAL')}
            </Pill>
          </Pills>

          {partner && (
            <AppNote hasApp={partnerHasApp}>
              {partnerHasApp
                ? T('This tenant has an app configured — it can receive stations, so the bidirectional contract is available.')
                : T('This tenant has no app configured: it does not receive stations. Only the one-way contract is possible.')}
            </AppNote>
          )}

          <Label>{T('Token validity')}</Label>
          <Pills>
            {TTLS.map((option) => (
              <Pill
                key={option}
                type="button"
                selected={ttl === option}
                onClick={() => setTtl(option)}
              >
                {TTL_LABEL[option]}
              </Pill>
            ))}
          </Pills>

          {failed && <AppNote hasApp={false}>{failed}</AppNote>}
      </div>
    </Modal>
  );
};
